import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, EnvelopeSimple, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";
import OtpInput from "@/components/OtpInput";

/**
 * Iter208.19 — Password dimenticata con due opzioni: email o SMS OTP.
 * Flow SMS:
 *   1. utente inserisce email
 *   2. sceglie "SMS" → chiama /auth/forgot-password-sms/init
 *   3. se ok → mostra numero mascherato + input OTP + campo new password
 *   4. submit → /auth/forgot-password-sms/verify
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("choose"); // choose | email_sent | sms_otp
  const [smsInit, setSmsInit] = useState(null); // {challenge_id, phone_masked, expires_in}
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [smsError, setSmsError] = useState("");

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setStage("email_sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova.");
    } finally { setLoading(false); }
  };

  const submitSmsInit = async () => {
    if (!email.trim()) { toast.error("Inserisci prima la tua email"); return; }
    setLoading(true); setSmsError("");
    try {
      const r = await api.post("/auth/forgot-password-sms/init", {
        email: email.trim().toLowerCase(),
      });
      if (r.data?.ok && r.data?.method === "sms") {
        setSmsInit(r.data);
        setStage("sms_otp");
        toast.success("Codice inviato via SMS");
      } else {
        setSmsError(r.data?.detail || "Numero di telefono non associato all'account");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova.");
    } finally { setLoading(false); }
  };

  const submitSmsVerify = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 5) { toast.error("Inserisci il codice OTP"); return; }
    if (!newPassword || newPassword.length < 8) { toast.error("Password minimo 8 caratteri"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password-sms/verify", {
        email: email.trim().toLowerCase(),
        challenge_id: smsInit.challenge_id,
        code: otp,
        new_password: newPassword,
      });
      toast.success("Password aggiornata. Ora puoi accedere.");
      setTimeout(() => { window.location.href = "/login"; }, 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido o scaduto.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 relative">
        <Link
          to="/"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-purple-700 transition-colors"
          data-testid="forgot-back-home"
        >
          <ArrowLeft size={14} weight="bold" />
          Torna alla home
        </Link>
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg ht-gradient flex items-center justify-center text-white font-bold">H</div>
            <div>
              <div className="font-display text-xl font-bold text-purple-800">Housetag.it</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-widest">Real Estate Suite</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold">Password dimenticata?</h1>

          {stage === "choose" && (
            <>
              <p className="text-sm text-muted-foreground mt-2">
                Inserisci la tua email e scegli come vuoi recuperare la password.
              </p>
              <form onSubmit={submitEmail} className="mt-6 space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" required value={email} onChange={e => { setEmail(e.target.value); setSmsError(""); }}
                    placeholder="la-tua@email.it" autoFocus data-testid="forgot-email" />
                </div>
                <Button type="submit" disabled={loading} className="w-full ht-gradient border-0" data-testid="forgot-submit-email">
                  <EnvelopeSimple size={16} weight="bold" className="mr-2" />
                  {loading ? "Invio in corso…" : "Invia email di recupero"}
                </Button>
                <div className="relative py-2 text-center">
                  <span className="text-[11px] text-muted-foreground bg-white px-2 relative z-10">oppure</span>
                  <div className="absolute inset-x-0 top-1/2 h-px bg-slate-200 -z-0" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={submitSmsInit}
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="forgot-submit-sms"
                >
                  <DeviceMobile size={16} weight="bold" className="mr-2" />
                  Invia codice via SMS
                </Button>
                {smsError && (
                  <div
                    className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed"
                    data-testid="forgot-sms-error"
                  >
                    {smsError}. Puoi comunque procedere con il recupero via email.
                  </div>
                )}
              </form>
            </>
          )}

          {stage === "email_sent" && (
            <>
              <p className="text-sm text-muted-foreground mt-2">
                Se l'indirizzo è registrato riceverai una email con le istruzioni per reimpostare la password. Controlla anche la casella spam.
              </p>
              <div className="mt-6 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
                ✅ Se l'email <strong>{email}</strong> è registrata, tra pochi istanti riceverai un messaggio con il link per reimpostare la password. Il link è valido per <strong>1 ora</strong>.
              </div>
            </>
          )}

          {stage === "sms_otp" && smsInit && (
            <>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Abbiamo inviato un codice a 6 cifre al numero <strong data-testid="forgot-phone-masked">{smsInit.phone_masked}</strong>. Il codice è valido per <strong>5 minuti</strong>.
              </p>
              <form onSubmit={submitSmsVerify} className="mt-6 space-y-4">
                <div>
                  <Label>Codice OTP</Label>
                  <OtpInput length={6} value={otp} onChange={setOtp} data-testid="forgot-otp" />
                </div>
                <div>
                  <Label>Nuova password</Label>
                  <Input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimo 8 caratteri"
                    data-testid="forgot-new-password"
                    minLength={8}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full ht-gradient border-0"
                  data-testid="forgot-sms-verify"
                >
                  {loading ? "Verifica in corso…" : "Imposta nuova password"}
                </Button>
                <button
                  type="button"
                  className="w-full text-xs text-slate-500 hover:text-purple-700 underline"
                  onClick={() => { setStage("choose"); setOtp(""); setNewPassword(""); setSmsInit(null); }}
                >
                  ← Torna alla scelta
                </button>
              </form>
            </>
          )}

          {/* Hint per utenti Google (sempre visibile: nessuna enumeration server-side) */}
          {stage !== "sms_otp" && (
            <div
              className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-2 leading-relaxed"
              data-testid="google-account-hint"
            >
              <svg viewBox="0 0 24 24" width={16} height={16} className="mt-0.5 shrink-0" aria-hidden="true">
                <path fill="#4285F4" d="M23.49 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.44c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.11C3.26 21.3 7.31 24 12 24z" />
                <path fill="#FBBC05" d="M5.27 14.26c-.24-.72-.38-1.49-.38-2.26s.14-1.54.38-2.26V6.63H1.28C.46 8.24 0 10.06 0 12s.46 3.76 1.28 5.37l3.99-3.11z" />
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.94 1.15 15.23 0 12 0 7.31 0 3.26 2.7 1.28 6.63l3.99 3.11C6.22 6.86 8.87 4.75 12 4.75z" />
              </svg>
              <div>
                <strong>Hai registrato l'account con Google?</strong> Non c'è una password interna Housetag da reimpostare.{" "}
                <Link to="/login" className="underline hover:text-blue-950 font-semibold">
                  Torna al login
                </Link>{" "}
                e usa il pulsante <strong>"Accedi con Google"</strong>.
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-muted-foreground">
            <Link to="/login" className="text-purple-700 hover:underline">← Torna al login</Link>
          </div>
        </div>
      </div>
      <div className="auth-image hidden md:block" />
    </div>
  );
}
